import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useRequireGoogleDrive } from 'hooks/useRequireGoogleDrive'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import {
  accountProfileImageReference,
  businessProfileImageReference,
  uploadProfileImageToDrive
} from 'services/Api/ProfileImageUpload'
import { resizeImage, validateImageMimeType } from 'utils'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { NotificationType } from 'pages/Notification/Notification.types'
import {
  broadcastEvent,
  setAccountProfileImage,
  setBusinessProfileImage
} from 'state/dashboard/dashboard.actions'
import { ENTITY_TYPE } from '../../constants'

export type ProfilePhotoTarget =
  | { kind: 'account'; entityType: ENTITY_TYPE; entityId: string }
  | { kind: 'business' }

// Android/iOS pick from camera/gallery; the browser has one file picker.
const ACCEPT = 'image/jpeg,image/png,image/webp'
const MAX_BYTES = 10 * 1024 * 1024

export interface UseProfilePhotoUploadResult {
  // Opens the file picker — after the Google Drive adoption nudge when backup is off
  // (KMP ShouldShowGDriveImageBackupScreen gate on the photo sheet)
  openPicker: () => void
  uploading: boolean
  // Clears the saved photo (Android/iOS "Delete": a blank value removes it on the backend)
  removePhoto: () => void
  // Local preview shown while the upload is in flight (KMP localProfileImagePath)
  previewUrl: string | null
  // Render both: the hidden <input type="file"> and the Drive nudge dialog
  input: React.ReactElement
  dialog: React.ReactElement
}

// Change a customer/supplier or business profile photo: gate on Google Drive backup, upload
// the photo to the merchant's Drive, then save the Drive URL on the account / business.
export const useProfilePhotoUpload = (
  target: ProfilePhotoTarget,
  // The photo URL currently in the store; when it changes after an upload the local preview
  // is dropped so the saved (Drive) image takes over
  currentUrl?: string
): UseProfilePhotoUploadResult => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { dialog, ensure } = useRequireGoogleDrive({
    message: t('dashboard:PhotoNeedsGoogleDrive')
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // Free the object URL when the preview changes or the component goes away
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const uploadedUrlRef = useRef<string | null>(null)
  useEffect(() => {
    if (uploadedUrlRef.current && currentUrl === uploadedUrlRef.current) {
      uploadedUrlRef.current = null
      setPreviewUrl(null)
    }
  }, [currentUrl])

  const targetName =
    target.kind === 'business'
      ? 'business'
      : target.entityType === 'customer'
      ? 'customer'
      : 'supplier'
  const track = (event: string, props: Record<string, unknown> = {}) =>
    dispatch(broadcastEvent(event, { target: targetName, ...props }))

  const toast = (type: NotificationType, bodyText: string) =>
    dispatch(addAutoFadeNotification({ type, bodyText }))

  const openPicker = useCallback(() => {
    if (uploading) {
      return
    }
    ensure(() => inputRef.current?.click())
  }, [ensure, uploading])

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files && e.target.files[0]
    // Allow picking the same file twice in a row
    e.target.value = ''
    if (!picked) {
      return
    }
    if (!validateImageMimeType(picked)) {
      track('profile_photo_upload_failed', { reason: 'invalid_type', mime: picked.type })
      toast(NotificationType.ERROR, t('dashboard:PhotoInvalidType'))
      return
    }
    if (picked.size > MAX_BYTES) {
      track('profile_photo_upload_failed', { reason: 'too_large', bytes: picked.size })
      toast(NotificationType.ERROR, t('dashboard:PhotoTooLarge'))
      return
    }
    setUploading(true)
    setPreviewUrl(URL.createObjectURL(picked))
    try {
      const file = await resizeImage(picked)
      const businessId = await AuthStorageInstance.getCurrentBusinessId()
      if (!businessId) {
        throw new Error('No active business')
      }
      const reference =
        target.kind === 'business'
          ? businessProfileImageReference(businessId)
          : accountProfileImageReference(businessId, target.entityType, target.entityId)
      // Mobile fires this when the picked file is saved locally before the Drive upload
      dispatch(broadcastEvent('local_file_created', { file_reference: reference }))
      const url = await uploadProfileImageToDrive(file, reference)
      if (!mountedRef.current) {
        return
      }
      if (!url) {
        track('profile_photo_upload_failed', { reason: 'upload_failed' })
        setPreviewUrl(null)
        toast(NotificationType.ERROR, t('dashboard:PhotoUploadFailed'))
        return
      }
      track('profile_photo_updated', { outcome: 'uploaded' })
      // Persisting (and the store update) happens in the dashboard saga; the preview stays
      // until the saved URL is in the store and the <img> swaps over.
      uploadedUrlRef.current = url
      if (target.kind === 'business') {
        dispatch(setBusinessProfileImage({ url }))
      } else {
        dispatch(
          setAccountProfileImage({ entityType: target.entityType, entityId: target.entityId, url })
        )
      }
    } catch (error) {
      console.error('Profile photo upload failed', error)
      track('profile_photo_upload_failed', { reason: 'error', error: String(error) })
      if (mountedRef.current) {
        setPreviewUrl(null)
        toast(NotificationType.ERROR, t('dashboard:PhotoUploadFailed'))
      }
    } finally {
      if (mountedRef.current) {
        setUploading(false)
      }
    }
  }

  const removePhoto = useCallback(() => {
    if (uploading) {
      return
    }
    setPreviewUrl(null)
    track('profile_photo_updated', { outcome: 'removed' })
    if (target.kind === 'business') {
      dispatch(setBusinessProfileImage({ url: '' }))
    } else {
      dispatch(
        setAccountProfileImage({
          entityType: target.entityType,
          entityId: target.entityId,
          url: ''
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading, dispatch, target.kind])

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      style={{ display: 'none' }}
      onChange={onFileChange}
      aria-label={t('dashboard:ChangePhoto')}
    />
  )

  return { openPicker, uploading, removePhoto, previewUrl, input, dialog }
}

export default useProfilePhotoUpload
